import styled from '@emotion/styled';
import { DeleteSweepRounded, LibraryMusicRounded, LoginRounded, SendRounded } from '@mui/icons-material';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '~/modules/Auth';
import {
  clearKaraokeBotChat,
  fetchKaraokeBotChat,
  getCachedKaraokeBotChat,
  KARAOKE_BOT_SENDER_ID,
  onKaraokeBotChatUpdated,
  resolveKaraokeBotUserId,
  sendKaraokeBotMessage,
  waitForKaraokeBotReply,
  type KaraokeBotChat,
  type KaraokeBotMessage,
} from '~/modules/KaraokeBot/karaokeBotService';

const SUGGESTIONS = ['Songs for a duet', '80s party hits', 'How do I warm up my voice?'];

function createWelcomeMessage(): KaraokeBotMessage {
  return {
    id: 'karaokebot-welcome',
    senderId: KARAOKE_BOT_SENDER_ID,
    fromMe: false,
    text: 'Hi, I am KaraokeBot 🎤 Ask me for song recommendations, artists, lyrics meaning or tips to sing better.',
    sentAt: new Date().toISOString(),
    time: '',
  };
}

function withWelcome(messages: KaraokeBotMessage[]): KaraokeBotMessage[] {
  return messages.length ? messages : [createWelcomeMessage()];
}

export default function KaraokeBotPanel() {
  const { user, token, isAuthenticated, isLoading } = useAuth();
  const currentUserId = useMemo(() => resolveKaraokeBotUserId(user), [user]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<KaraokeBotMessage[]>(() => [createWelcomeMessage()]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const canUseBot = Boolean(token && currentUserId && isAuthenticated);
  const hasConversation = messages.some((message) => message.fromMe);

  const applyChat = useCallback((chat: KaraokeBotChat) => {
    setChatId(chat.id);
    setMessages(withWelcome(chat.messages));
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      const node = messagesRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
  }, [messages, sending]);

  useEffect(() => {
    if (!canUseBot || !token || !currentUserId) {
      setChatId(null);
      setMessages([createWelcomeMessage()]);
      setError(null);
      return;
    }

    let cancelled = false;
    const cached = getCachedKaraokeBotChat(currentUserId);
    if (cached) applyChat(cached);
    else setLoading(true);

    fetchKaraokeBotChat(token, currentUserId)
      .then((chat) => {
        if (!cancelled) applyChat(chat);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load KaraokeBot.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyChat, canUseBot, currentUserId, token]);

  // Keeps the desktop and mobile instances of the panel in sync.
  useEffect(() => {
    if (!currentUserId) return;
    return onKaraokeBotChatUpdated(currentUserId, applyChat);
  }, [applyChat, currentUserId]);

  const sendMessage = async (text: string) => {
    const cleanMessage = text.trim();
    if (!cleanMessage || !token || !currentUserId || sending) return;

    setDraft('');
    setSending(true);
    setError(null);

    try {
      const targetChatId = chatId ?? (await fetchKaraokeBotChat(token, currentUserId)).id;
      const userMessageId = await sendKaraokeBotMessage(token, currentUserId, targetChatId, cleanMessage);
      await waitForKaraokeBotReply(token, currentUserId, userMessageId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the message to KaraokeBot.');
    } finally {
      setSending(false);
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMessage(draft);
  };

  const clearChat = async () => {
    if (!token || !currentUserId || sending) return;
    setError(null);

    try {
      await clearKaraokeBotChat(token, currentUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not clear the chat.');
    }
  };

  const openLogin = () => {
    window.dispatchEvent(new CustomEvent('karaoke:open-auth', { detail: 'login' }));
  };

  return (
    <Panel aria-label="KaraokeBot chat">
      <Header>
        <BotAvatar aria-hidden="true">
          <LibraryMusicRounded fontSize="inherit" />
        </BotAvatar>
        <HeaderCopy>
          <Title>KaraokeBot</Title>
          <Subtitle>
            {sending ? 'Preparing response...' : canUseBot ? 'Your music assistant' : 'WalkyCam login required'}
          </Subtitle>
        </HeaderCopy>
        {canUseBot && (
          <IconButton
            type="button"
            onClick={() => void clearChat()}
            aria-label="Clear KaraokeBot chat"
            title="Clear chat"
            disabled={loading || sending || !hasConversation}>
            <DeleteSweepRounded fontSize="small" />
          </IconButton>
        )}
      </Header>

      {!canUseBot && !isLoading ? (
        <EmptyState>
          <LibraryMusicRounded sx={{ fontSize: 42 }} />
          <strong>Connect your WalkyCam account</strong>
          <span>KaraokeBot is available for signed-in users so it can keep your conversation synced.</span>
          <LoginButton type="button" onClick={openLogin}>
            <LoginRounded fontSize="small" />
            <span>Login</span>
          </LoginButton>
        </EmptyState>
      ) : (
        <>
          <Messages ref={messagesRef} aria-live="polite">
            {loading ? (
              <LoadingLine>Loading KaraokeBot...</LoadingLine>
            ) : (
              messages.map((message) => (
                <MessageRow key={message.id} fromMe={message.fromMe}>
                  <Bubble fromMe={message.fromMe}>
                    <MessageText>{message.text}</MessageText>
                    {message.time && <MessageTime>{message.time}</MessageTime>}
                  </Bubble>
                </MessageRow>
              ))
            )}

            {!loading && !hasConversation && !sending && (
              <Suggestions>
                {SUGGESTIONS.map((suggestion) => (
                  <SuggestionChip
                    key={suggestion}
                    type="button"
                    disabled={!canUseBot}
                    onClick={() => void sendMessage(suggestion)}>
                    {suggestion}
                  </SuggestionChip>
                ))}
              </Suggestions>
            )}

            {sending && (
              <MessageRow fromMe={false}>
                <TypingBubble aria-label="KaraokeBot is typing">
                  <span />
                  <span />
                  <span />
                </TypingBubble>
              </MessageRow>
            )}
          </Messages>

          {error && <ErrorLine role="alert">{error}</ErrorLine>}

          <Composer onSubmit={submit}>
            <ComposerInput
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={sending ? 'KaraokeBot is replying...' : 'Ask about songs, artists, singing...'}
              maxLength={1000}
              disabled={loading || sending || !canUseBot}
            />
            <SendButton
              type="submit"
              disabled={!draft.trim() || loading || sending || !canUseBot}
              aria-label="Send message">
              <SendRounded fontSize="small" />
            </SendButton>
          </Composer>
        </>
      )}
    </Panel>
  );
}

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SuggestionChip = styled.button`
  padding: 7px 12px;
  border: 1px solid rgba(244, 90, 10, 0.45);
  border-radius: 999px;
  background: rgba(244, 90, 10, 0.1);
  color: var(--walky-ink);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: rgba(244, 90, 10, 0.2);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Panel = styled.section`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: color-mix(in srgb, var(--walky-surface) 92%, #000 8%);
  color: var(--walky-ink);
`;

const Header = styled.header`
  flex: 0 0 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--walky-orange);
  color: #fff;
`;

const BotAvatar = styled.div`
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  font-size: 21px;
`;

const HeaderCopy = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: 0;
`;

const Subtitle = styled.p`
  margin: 3px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconButton = styled.button`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.55;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: rgba(255, 255, 255, 0.28);
    outline: none;
  }

  @media (max-width: 560px) {
    width: 42px;
    height: 42px;
    flex-basis: 42px;
  }
`;

const Messages = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 12px;
  background:
    radial-gradient(circle at 18% 0%, rgba(244, 90, 10, 0.1), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.03), rgba(0, 0, 0, 0.08));
`;

const MessageRow = styled.div<{ fromMe: boolean }>`
  display: flex;
  justify-content: ${({ fromMe }) => (fromMe ? 'flex-end' : 'flex-start')};
`;

const Bubble = styled.div<{ fromMe: boolean }>`
  max-width: 88%;
  min-width: 0;
  padding: 9px 12px 6px;
  border-radius: ${({ fromMe }) => (fromMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px')};
  background: ${({ fromMe }) => (fromMe ? 'linear-gradient(135deg, var(--walky-orange), #ff7a1a)' : '#ffffff')};
  color: ${({ fromMe }) => (fromMe ? '#ffffff' : '#1f2937')};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
  font-size: 14px;
  line-height: 1.45;
`;

const MessageText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const MessageTime = styled.span`
  display: block;
  margin-top: 3px;
  text-align: right;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 700;
  opacity: 0.72;
`;

const LoadingLine = styled.div`
  margin: auto;
  color: var(--walky-muted);
  font-size: 13px;
  font-weight: 800;
`;

const TypingBubble = styled.div`
  min-width: 58px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 16px 16px 16px 4px;
  background: #fff;
  color: #6b7280;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);

  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #9ca3af;
    animation: karaokeBotTyping 1.1s infinite ease-in-out;
  }

  span:nth-of-type(2) {
    animation-delay: 0.15s;
  }

  span:nth-of-type(3) {
    animation-delay: 0.3s;
  }

  @keyframes karaokeBotTyping {
    0%,
    60%,
    100% {
      transform: translateY(0);
      opacity: 0.55;
    }

    30% {
      transform: translateY(-4px);
      opacity: 1;
    }
  }
`;

const Composer = styled.form`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--walky-border);
  background: rgba(255, 255, 255, 0.9);
`;

const ComposerInput = styled.input`
  min-width: 0;
  flex: 1 1 auto;
  height: 42px;
  padding: 0 14px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  outline: none;

  &:focus {
    border-color: var(--walky-orange);
    box-shadow: 0 0 0 3px rgba(244, 90, 10, 0.18);
  }

  &:disabled {
    cursor: wait;
    color: #6b7280;
    background: #f3f4f6;
  }
`;

const SendButton = styled.button`
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--walky-orange);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(244, 90, 10, 0.24);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
    box-shadow: none;
  }

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    filter: brightness(1.06);
    outline: none;
  }
`;

const EmptyState = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 22px 18px;
  text-align: center;
  color: var(--walky-muted);

  strong {
    color: var(--walky-ink);
    font-size: 15px;
    line-height: 1.2;
  }

  span {
    max-width: 220px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.45;
  }
`;

const LoginButton = styled.button`
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 4px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--walky-orange);
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
`;

const ErrorLine = styled.div`
  flex: 0 0 auto;
  padding: 8px 10px;
  border-top: 1px solid rgba(244, 90, 10, 0.25);
  background: rgba(244, 90, 10, 0.12);
  color: var(--walky-ink);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
`;
